// Pure helpers to convert between a uint8 `dali_address` byte and the editor's
// (mode + id) representation. Mirrors `addressParse` in decoder_ul20xx_1_1_x.js
// (plan §3): 0x01 analog, 0xFE broadcast, 0xFF all devices; group iff
// (addr & 0x80) -> id = (addr >> 1) & 0xF; single iff !(addr & 0x80) &&
// !(addr & 0x01) -> id = (addr >> 1) & 0x3F.

import {
  DALI_ALL_DEVICES,
  DALI_ANALOG,
  DALI_BROADCAST,
  DaliAddressMode,
} from '../types/dimmingProfile.types'

export interface DaliAddressValue {
  mode: DaliAddressMode
  id: number // meaningful only for 'single' (0..63) and 'group' (0..15)
}

// Decode a uint8 address byte into (mode, id).
export function parseDaliAddress(addr: number): DaliAddressValue {
  if (addr === DALI_ANALOG) return { mode: 'analog', id: 0 }
  if (addr === DALI_BROADCAST) return { mode: 'broadcast', id: 0 }
  if (addr === DALI_ALL_DEVICES) return { mode: 'all', id: 0 }
  if (addr & 0x80) {
    // Group address: id = (addr >> 1) & 0xF
    return { mode: 'group', id: (addr >> 1) & 0xf }
  }
  // Single address: id = (addr >> 1) & 0x3F
  return { mode: 'single', id: (addr >> 1) & 0x3f }
}

// Encode (mode, id) back into a uint8 address byte.
export function buildDaliAddress(value: DaliAddressValue): number {
  switch (value.mode) {
    case 'analog':
      return DALI_ANALOG
    case 'broadcast':
      return DALI_BROADCAST
    case 'all':
      return DALI_ALL_DEVICES
    case 'group':
      // Group: bit7 set, id in bits 4..1.
      return 0x80 | ((value.id & 0xf) << 1)
    case 'single':
      // Single: bit7 clear, bit0 clear, id in bits 6..1.
      return (value.id & 0x3f) << 1
    default:
      return DALI_ALL_DEVICES
  }
}
